import "./CmsPublicView.css";
import stegLogo from "../../assets/images/logo.png";
import { trackPublicPage } from "../services/visitor-monitoring";
import KnowledgeSearch from "../components/KnowledgeSearch";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Toolbar,
  Typography,
  Checkbox,
  TextField,
  useMediaQuery,
  useScrollTrigger,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import React, { useEffect, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";

import CmsBlockRenderer from "../components/CmsBlockRenderer";
import {
  MenuItem,
  PageContent,
  RootURL,
  isAuthenticatedPreview,
  Site,
  SiteTeamMember,
  adminGetPublicTeamMembers,
  getPublicPage,
  getPublicPages,
  getPublicSiteByProjectNumber,
  getPublicSiteByUrlKey,
  getPublicSiteNavigation,
  submitContactForm,
} from "../services/cms-api";

const upsertHeadTag = (
  selector: string,
  attributes: Record<string, string>
) => {
  let tag = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement("meta");
    document.head.appendChild(tag);
  }
  Object.entries(attributes).forEach(([name, value]) =>
    tag!.setAttribute(name, value)
  );
};

const plainText = (value = "") =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// The preview host is access-restricted and its API deliberately returns
// drafts.  Do not discard those pages again in the browser.
const isPreview = isAuthenticatedPreview || RootURL === "/preview-api/";
const isVisibleInCurrentMode = (page: PageContent | null | undefined) =>
  Boolean(
    page &&
      (isPreview ||
        (page.is_published !== false && page.isPublished !== false))
  );

const pageSummary = (page: PageContent | null, site: Site) => {
  const firstTextBlock = page?.blocks?.find((block) =>
    ["html", "text"].includes(String(block.blockType).toLowerCase())
  );
  const summary = plainText(firstTextBlock?.content || site.description || "");
  return (
    summary || `Informationen und Beteiligungsmöglichkeiten von ${site.title}.`
  ).slice(0, 155);
};

function ScrollReveal({
  children,
  delay = 0,
  eager = false,
}: {
  children: React.ReactNode;
  delay?: number;
  eager?: boolean;
}) {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(eager);

  useEffect(() => {
    if (eager) {
      setIsVisible(true);
      return;
    }

    const element = elementRef.current;
    if (!element) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      {
        threshold: 0,
        rootMargin: "0px 0px -24px 0px",
      }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [eager]);

  return (
    <div
      ref={elementRef}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "none" : "translateY(18px)",
        transition: `opacity 450ms ease ${delay}ms, transform 450ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: isVisible ? "auto" : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

class PublicBlockBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box p={3} bgcolor="#fff" borderRadius={2}>
          <Typography color="textSecondary">
            Dieser Inhaltsbereich konnte nicht angezeigt werden. Bitte laden Sie die Seite erneut.
          </Typography>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default function CmsPublicView({
  siteProp,
  mandantKeyProp,
}: {
  siteProp?: Site;
  mandantKeyProp?: string;
}) {
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const showBackToTop = useScrollTrigger({ disableHysteresis: true, threshold: 300 });
  const { mandantKey, pageSlug, gemeindeId, projectNumber, siteSlug, id } =
    useParams<{
      mandantKey?: string;
      pageSlug?: string;
      gemeindeId?: string;
      projectNumber?: string;
      siteSlug?: string;
      id?: string;
    }>();

  const [site, setSite] = useState<Site | null>(null);
  const [page, setPage] = useState<PageContent | null>(null);
  const [navigation, setNavigation] = useState<MenuItem[]>([]);
  const [teamMembersByBlock, setTeamMembersByBlock] = useState<
    Record<number, SiteTeamMember[]>
  >({});
  const [allPages, setAllPages] = useState<PageContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState<MenuItem | null>(null);
  const [expandedMobileMenus, setExpandedMobileMenus] = useState<number[]>([]);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactSending, setContactSending] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactStatus, setContactStatus] = useState<string | null>(null);
  const [contactForm, setContactForm] = useState({ firstName: "", lastName: "", email: "", companyName: "", address: "", title: "", message: "", rulesAccepted: false });
  const [website, setWebsite] = useState("");

  const hostname = window.location.hostname;
  const isTestEnvironment = hostname === "beteiligung.steg-gis.de";
  const hasFooterContact = Boolean(site?.has_contact_form ?? site?.hasContactForm);

  const sendContactMessage = async () => {
    if (!contactForm.rulesAccepted) { setContactStatus("Bitte bestätigen Sie, dass Sie die Datenschutzerklärung zur Kenntnis genommen haben."); return; }
    if (!site?.id) { setContactStatus("Das Kontaktformular wurde noch nicht eingerichtet."); return; }
    setContactSending(true); setContactStatus(null); setContactSubmitted(false);
    try {
      const result = await submitContactForm({ site: site!.id, first_name: contactForm.firstName, last_name: contactForm.lastName, email: contactForm.email, company_name: contactForm.companyName, address: contactForm.address, title: contactForm.title, message: contactForm.message, rules_accepted: true, website });
      const referenceId = result.referenceId || result.reference_id;
      setContactStatus(`Vielen Dank. Ihre Nachricht wurde übermittelt. Ihre Referenznummer: ${referenceId || "wird erstellt"}. Bitte geben Sie diese bei Rückfragen an.`);
      setContactSubmitted(true);
      setContactForm({ firstName: "", lastName: "", email: "", companyName: "", address: "", title: "", message: "", rulesAccepted: false });
    } catch { setContactStatus("Die Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es später erneut."); }
    finally { setContactSending(false); }
  };

  const findParentPageForCard = (cardPageId: number | string) => {
    return allPages.find((parentPage) =>
      parentPage.blocks?.some(
        (block: any) =>
          block.blockType === "page_cards" &&
          block.settings?.cards?.some((card: any) => {
            if (String(card.pageId) !== String(cardPageId)) return false;
            // Check if this page itself is published
            const linkedPage = allPages.find(
              (p) => String(p.id) === String(cardPageId)
            );
            return isVisibleInCurrentMode(linkedPage);
          })
      )
    );
  };

  // A card can open a page that is not itself part of the navigation.  In
  // that case, keep the navigation entry of the page containing the card
  // active (for example, "Veranstaltungen" for an event detail page).
  const findMenuItemForPage = (
    items: MenuItem[],
    pageId: number | string
  ): MenuItem | undefined => {
    for (const item of items) {
      if (String(item.page) === String(pageId)) return item;
      const childMatch = item.children && findMenuItemForPage(item.children, pageId);
      if (childMatch) return childMatch;
    }
    return undefined;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const standaloneUrlKey =
          siteSlug ||
          (mandantKey && projectNumber?.startsWith("site_")
            ? projectNumber
            : undefined);
        const isStandaloneSite = Boolean(standaloneUrlKey);
        const identifier = standaloneUrlKey || projectNumber || id;
        if (!identifier) {
          setError("Webseite nicht gefunden");
          setLoading(false);
          return;
        }

        // Project sites retain the established project lookup. Standalone
        // sites are resolved exclusively by their globally unique URL key.
        const currentSite = isStandaloneSite
          ? await getPublicSiteByUrlKey(standaloneUrlKey!)
          : await getPublicSiteByProjectNumber(identifier);
        if (!currentSite) {
          setError("Webseite nicht gefunden");
          setLoading(false);
          return;
        }

        setSite(currentSite);

        // 2. Fetch navigation structure through the matching site lookup.
        const navData = await getPublicSiteNavigation(currentSite.id);
        setNavigation(navData);

        // 3. Fetch all pages (needed for Page Cards)
        try {
          const pagesData = await getPublicPages(currentSite.id);
          setAllPages(pagesData);
        } catch {
          setAllPages([]);
        }
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setError("404");
        } else {
          setError("Webseite konnte nicht geladen werden.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mandantKey, gemeindeId, projectNumber, siteSlug, id]);

  useEffect(() => {
    if (!site || navigation.length === 0) return;

    let cancelled = false;
    const loadPage = async (identifier: number | string) => {
      // The authenticated list already contains the full draft page and blocks.
      const cached = isPreview && allPages.find(candidate =>
        String(candidate.id) === String(identifier) ||
        candidate.urlKey === String(identifier) || candidate.url_key === String(identifier)
      );
      return cached || getPublicPage(identifier);
    };
    const fetchPageData = async () => {
      const navigationKey = history.location.key;
      const navigationPath = history.location.pathname;
      setLoadingPage(true);
      setPageError(null);
      try {
        let targetSlug = pageSlug;

        // If no slug is provided, we should redirect to the first tab's slug
        if (!targetSlug && navigation.length > 0) {
          const firstSlug = navigation[0].urlKey;
          if (firstSlug) {
            const basePath = getBasePath();

            history.replace(`${basePath}/${firstSlug}`);
            return; // Exit and let the route change trigger a new effect run
          }
        }

        if (!targetSlug) {
          targetSlug = "start";
        }

        const targetSlugLower = targetSlug.toLowerCase();

        const siteId = site?.id;
        if (!siteId) {
          setLoadingPage(false);
          return;
        }

        const findInMenu = (items: MenuItem[]): MenuItem | undefined => {
          for (const item of items) {
            if (item.urlKey === targetSlug || item.urlKey === targetSlugLower) {
              return item;
            }
            if (item.children && item.children.length > 0) {
              const found = findInMenu(item.children);
              if (found) return found;
            }
          }
          return undefined;
        };

        let menuItem = findInMenu(navigation);

        // 2. If no matching menu item and targetSlug is "start", use first menu item
        if (!menuItem && targetSlugLower === "start" && navigation.length > 0) {
          menuItem = navigation[0];
        }

        if (menuItem) {
          setActiveMenuItem(menuItem);
        }

        // 3. If no matching menu item, search in allPages (for card subpages etc.)
        if (!menuItem) {
          const matchedPage = allPages.find((p) => {
            const slug = p.urlKey || p.url_key || p.slug;
            return (slug === targetSlug || slug === targetSlugLower) && isVisibleInCurrentMode(p);
          });

          if (matchedPage) {
            const parentPage = findParentPageForCard(matchedPage.id);
            if (parentPage) {
              const parentMenuItem = findMenuItemForPage(
                navigation,
                parentPage.id
              );
              if (parentMenuItem) {
                setActiveMenuItem(parentMenuItem);
              }
            } else {
              setActiveMenuItem(null);
            }

            const pageData = await loadPage(matchedPage.id);
            if (isVisibleInCurrentMode(pageData)) {
              if (!cancelled && history.location.key === navigationKey && history.location.pathname === navigationPath) {
                setPage(pageData);
                if (!isPreview) trackPublicPage(pageData.id, String(navigationKey || "") + navigationPath);
              }
            } else {
              if (!cancelled) setPage(null);
            }
            return;
          }
        }

        // 1. If we have a menuItem with a page ID, use it
        if (menuItem && menuItem.page) {
          const pageData = await loadPage(menuItem.page);
          if (isVisibleInCurrentMode(pageData)) {
            if (!cancelled && history.location.key === navigationKey && history.location.pathname === navigationPath) {
                setPage(pageData);
                if (!isPreview) trackPublicPage(pageData.id, String(navigationKey || "") + navigationPath);
              }
          } else {
            if (!cancelled) setPage(null);
          }
        } else if (menuItem && menuItem.urlKey) {
          const pageData = await loadPage(menuItem.page || menuItem.urlKey);
          if (isVisibleInCurrentMode(pageData)) {
            if (!cancelled && history.location.key === navigationKey && history.location.pathname === navigationPath) {
                setPage(pageData);
                if (!isPreview) trackPublicPage(pageData.id, String(navigationKey || "") + navigationPath);
              }
          } else {
            if (!cancelled) setPage(null);
          }
        } else if (targetSlug === "home" || targetSlug === "start") {
          // Fallback for home/start if no menu item matches
          try {
            const firstPage = allPages.find((candidate) =>
              [candidate.urlKey, candidate.url_key, candidate.slug].includes(targetSlug)
            );
            if (!firstPage) throw new Error("Public page not found");
            const pageData = await loadPage(firstPage.id);
            if (isVisibleInCurrentMode(pageData)) {
              if (!cancelled && history.location.key === navigationKey && history.location.pathname === navigationPath) {
                setPage(pageData);
                if (!isPreview) trackPublicPage(pageData.id, String(navigationKey || "") + navigationPath);
              }
            } else {
              if (!cancelled) setPage(null);
            }
          } catch (e) {
            if (!cancelled) setPage(null);
          }
        } else {
          if (!cancelled) setPage(null);
        }
      } catch (error) {
        if (!cancelled) {
          setPage(null);
          setPageError("Der Seiteninhalt konnte nicht geladen werden. Bitte erneut versuchen.");
          console.error("CMS page could not be loaded", { pageSlug, error });
        }
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    };

    fetchPageData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    site,
    pageSlug,
    navigation,
    gemeindeId,
    projectNumber,
    siteSlug,
    id,
    history,
    allPages,
  ]);

  useEffect(() => {
    if (!site || !page) return;

    const teamBlocks = page.blocks.filter(
      (block) => block.blockType === "person_list"
    );
    if (!teamBlocks.length) {
      setTeamMembersByBlock({});
      return;
    }

    Promise.all(
      teamBlocks.map(
        async (block) =>
          [
            block.id,
            await adminGetPublicTeamMembers(site.id, block.id),
          ] as const
      )
    )
      .then((entries) => setTeamMembersByBlock(Object.fromEntries(entries)))
      .catch(() => {
        setTeamMembersByBlock({});
      });
  }, [site, page]);

  const toggleMobileMenu = (itemId: number) => {
    setExpandedMobileMenus((prev) => (prev.includes(itemId) ? [] : [itemId]));
  };

  const activePageSlug = pageSlug || "start";

  const primaryColor = site?.primaryColor || "#00b5e2";

  const getLogoUrl = (url?: string) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    const base = RootURL.replace("/cms/", "");
    return `${base.endsWith("/") ? base.slice(0, -1) : base}${
      url.startsWith("/") ? url : `/${url}`
    }`;
  };

  const getBasePath = () => {
    const identifier = projectNumber || siteSlug || id;
    const mandantIdentifier = mandantKeyProp || mandantKey || gemeindeId;

    if (mandantIdentifier && identifier) {
      return `/${mandantIdentifier}/${identifier}`;
    }

    return `/p/${identifier}`;
  };

  const getLinkTo = (item: MenuItem) => {
    const itemPageSlug = item.urlKey;
    return `${getBasePath()}/${itemPageSlug}`;
  };

  const getRootLink = () => {
    const basePath = getBasePath();

    if (navigation.length > 0) {
      const firstItem = navigation[0];
      const slug = firstItem.urlKey || "";
      return `${basePath}/${slug}`;
    }
    return `${basePath}/`;
  };

  // Find active top level item to show subtabs
  const activeTopLevelItem =
    navigation.find((item) => {
      // Check if the item itself is active
      const isItemActive =
        item.urlKey === activePageSlug ||
        item.urlKey === activePageSlug.toLowerCase();
      if (isItemActive) return true;

      // Check if any of its children are active
      const isChildActive = item.children?.some(
        (child) =>
          child.urlKey === activePageSlug ||
          child.urlKey === activePageSlug.toLowerCase()
      );
      if (isChildActive) return true;

      // Also check if it's the parent of the currently activeMenuItem
      if (activeMenuItem && activeMenuItem.parent === item.id) {
        return true;
      }

      return false;
    }) || activeMenuItem;

  useEffect(() => {
    if (
      activeTopLevelItem &&
      !expandedMobileMenus.includes(activeTopLevelItem.id)
    ) {
      setExpandedMobileMenus((prev) => [...prev, activeTopLevelItem.id]);
    }
  }, [activeTopLevelItem, expandedMobileMenus]);

  useEffect(() => {
    if (!site) return;

    const currentTitle =
      activeMenuItem?.title || page?.title || site.title || "Startseite";
    const titleParts = [
      currentTitle,
      site.title,
      "die STEG Beteiligungsplattform",
    ];
    document.title = Array.from(new Set(titleParts.filter(Boolean))).join(
      " | "
    );
    const description = pageSummary(page, site);
    const canonicalUrl = `${window.location.origin}${window.location.pathname}`;

    upsertHeadTag('meta[name="description"]', {
      name: "description",
      content: description,
    });
    upsertHeadTag('meta[property="og:title"]', {
      property: "og:title",
      content: document.title,
    });
    upsertHeadTag('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    upsertHeadTag('meta[property="og:type"]', {
      property: "og:type",
      content: "website",
    });
    let canonical = document.head.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    if (site.logo) {
      let favicon = document.querySelector(
        "link[rel='icon']"
      ) as HTMLLinkElement;

      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.appendChild(favicon);
      }

      favicon.href = getLogoUrl(site.logo);
    }
  }, [site, page, activeMenuItem]);

  const useStyles = makeStyles((t) => ({
    hero: {
      position: "relative",
      "&.has-image": {
        minHeight: 180,
        display: "flex",
        alignItems: "flex-end",
        color: "#fff",
        backgroundColor: "#26343f",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: "linear-gradient(0deg, rgba(15,25,33,.85), rgba(15,25,33,.38) 70%, rgba(15,25,33,.12))",
          pointerEvents: "none",
        },
        "& .project-title h1": { color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,.45)" },
        "& .project-title p": { color: "#f0f3f5 !important" },
      },
      backgroundColor: "#fff",
      color: "#26343f",
      borderBottom: "1px solid #e5e9ed",
    },
    heroContent: {
      position: "relative",
      zIndex: 1,
      boxSizing: "border-box",
      maxWidth: 1600,
      width: "100%",
      padding: "0 24px",
    },
    heroText: {
      minWidth: 0,
      padding: "20px 0",
      display: "grid",
      gridTemplateColumns: "auto minmax(0, 1fr)",
      columnGap: 20,
      alignItems: "center",
      "& > .project-title": { minWidth: 0 },
      [t.breakpoints.down("xs")]: {
        padding: "12px 0",
        columnGap: 20,
      },
    },
    heroImage: {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      objectPosition: "center",
      display: "block",

    },
    stickyNav: {
      position: "sticky",
      top: 0,
      zIndex: 1000,
      backgroundColor: "#fff",
      boxShadow: "none",
      borderBottom: "1px solid #f0f0f0",
    },
    navPill: {
      borderRadius: 0,
      padding: "14px 20px 12px",
      margin: "0 2px",
      textTransform: "none",
      fontWeight: 800,
      fontSize: "0.95rem",
      transition: "background-color 180ms ease, color 180ms ease",
      color: "#444",
      position: "relative",
      backgroundColor: "transparent",
      boxShadow: "none",
      borderBottom: "2px solid transparent",
      "&::after": {
        content: '\"\"',
        position: "absolute",
        bottom: -2,
        left: 0,
        right: 0,
        height: 2,
        backgroundColor: (props: { primaryColor: string }) => props.primaryColor,
        transform: "scaleX(0)",
        transition: "transform 200ms ease",
      },
      "@media (prefers-reduced-motion: reduce)": {
        transition: "none",
        "&::after": { transition: "none" },
      },
      "&:hover": {
        backgroundColor: (props: {
          primaryColor: string;
          headerImage?: string;
        }) => `${props.primaryColor}10`,
        color: (props: { primaryColor: string; headerImage?: string }) =>
          props.primaryColor,
      },
      "&.active": {
        backgroundColor: "transparent",
        color: (props: { primaryColor: string; headerImage?: string }) =>
          props.primaryColor,
        boxShadow: "none",
        "&::after": { transform: "scaleX(1)" },
        "&:hover": {
          backgroundColor: (props: {
            primaryColor: string;
            headerImage?: string;
          }) => `${props.primaryColor}10`,
        },
      },
    },
    subNavLink: {
      borderRadius: 0,
      padding: "0 20px",
      margin: "0 2px",
      textTransform: "none",
      fontWeight: 800,
      fontSize: "0.95rem",
      whiteSpace: "nowrap",
      flexShrink: 0,
      transition: "all 0.2s ease",
      color: "#444",
      position: "relative",
      backgroundColor: "transparent",
      boxShadow: "none",
      borderBottom: "2px solid transparent",
      height: 52,
      "&:hover": {
        backgroundColor: (props: { primaryColor: string }) =>
          `${props.primaryColor}10`,
        color: (props: { primaryColor: string }) => props.primaryColor,
      },
      "&.active": {
        backgroundColor: "transparent",
        color: (props: { primaryColor: string }) => props.primaryColor,
        boxShadow: "none",
        borderBottom: (props: { primaryColor: string }) =>
          `4px solid ${props.primaryColor}`,
        fontWeight: 800,
        "&:hover": {
          backgroundColor: (props: { primaryColor: string }) =>
            `${props.primaryColor}10`,
        },
      },
    },
    subNavBar: {
      backgroundColor: "#fff",
      borderTop: "1px solid #f0f0f0",
      borderBottom: "1px solid #f0f0f0",
      // Keep subpages on one row, but let the whole bar scroll horizontally.
      // Scrolling the outer bar avoids the last item being clipped by the
      // centred container when there are many subpages.
      minHeight: 52,
      height: 52,
      display: "flex",
      alignItems: "stretch",
      overflowX: "auto",
      overflowY: "hidden",
      WebkitOverflowScrolling: "touch",
      boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
      [t.breakpoints.down("sm")]: {
        minHeight: 48,
        height: 48,
      },
    },
    section: {
      padding: t.spacing(2, 0, 4),
      backgroundColor: "#f7f8fa",
      [t.breakpoints.down("sm")]: {
        padding: t.spacing(2, 0, 3),
      },
    },
    footer: {
      backgroundColor: "#ffffff",
      borderTop: "1px solid rgba(0,0,0,0.08)",
      padding: "24px 32px",
      [t.breakpoints.down("sm")]: {
        padding: "32px 24px",
      },
      fontSize: "14px",
      color: "#555",
      "& a": {
        color: "inherit",
        textDecoration: "none",
        transition: "all 0.2s ease",
        "&:hover": {
          color: (props: { primaryColor: string }) => props.primaryColor,
        },
      },
    },
    footerLogo: {
      height: 32,
      transition: "all 0.2s ease",
      "&:hover": {
        transform: "translateY(-2px)",
      },
    },
    topLogoContainer: {
      width: 68,
      height: 68,
      padding: 8,
      boxSizing: "border-box",
      backgroundColor: "#fff",
      borderRadius: 6,
      marginBottom: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    topLogo: {
      width: "100%",
      height: "100%",
      objectFit: "contain",
    },
  }));

  const classes = useStyles({
    primaryColor,
    headerImage: site?.headerImage,
  });

  // When a second navigation level is visible, keep the primary row compact
  // so the navigation does not dominate the start of the page.
  const hasSubNavigation = Boolean(activeTopLevelItem?.children?.length);

  const navLinks = (
    <Box
      display="flex"
      alignItems="center"
      flexWrap="nowrap"
      style={{ overflowX: "auto" }}
    >
      {navigation.map((item) => {
        const isActive =
          activeTopLevelItem?.id === item.id ||
          item.urlKey?.toLowerCase() === activePageSlug.toLowerCase();
        return (
          <Button
            key={item.id}
            component={Link}
            to={getLinkTo(item)}
            className={`steg-project-tab ${classes.navPill} ${isActive ? "active" : ""}`}
            style={{
              whiteSpace: "nowrap",
              ...(hasSubNavigation
                ? { padding: "10px 20px 7px", fontSize: "0.9rem" }
                : {}),
            }}
            onClick={() => {
              setActiveMenuItem(item);
            }}
          >
            {item.title}
          </Button>
        );
      })}
    </Box>
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error === "404" && !isAuthenticatedPreview) {
    // This will let the parent component know it should fallback to legacy
    return <Box display="none">404 - Falling back</Box>;
  }

  if (error || !site) {
    return (
      <Container maxWidth="lg">
        <Box py={10} textAlign="center">
          <Typography variant="h4" gutterBottom>
            {error === "404" ? "Vorschauseite nicht gefunden. Bitte prüfen Sie die CMS-Zuordnung." : error || "Seite nicht gefunden"}
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            style={{ marginBottom: 32 }}
          >
            Die von Ihnen angeforderte Seite konnte leider nicht geladen werden.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => (window.location.href = "/")}
            style={{ borderRadius: 24, padding: "10px 32px" }}
          >
            Zurück zur Startseite
          </Button>
        </Box>
      </Container>
    );
  }

  const subNavLinks = activeTopLevelItem?.children?.length ? (
    <Box className={classes.subNavBar}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{
          minHeight: 52,
          minWidth: "max-content",
          width: "100%",
          padding: "0 24px",
        }}
      >
            {activeTopLevelItem.children.map((child) => {
              const isChildActive =
                activePageSlug === child.urlKey ||
                child.urlKey?.toLowerCase() === activePageSlug.toLowerCase();
              return (
                <Button
                  key={child.id}
                  component={Link}
                  to={getLinkTo(child)}
                  className={`steg-project-tab ${classes.subNavLink} ${
                    isChildActive ? "active" : ""
                  }`}
                  aria-current={isChildActive ? "page" : undefined}
                  disableRipple={!isMobile}
                  onClick={() => {
                    setActiveMenuItem(child);
                  }}
                >
                  {child.title}
                </Button>
              );
            })}
      </Box>
    </Box>
  ) : null;

  // Separate a trailing street designation without changing the CMS title.
  const titleParts = site.title.match(/^(.+?)\s+[-–]\s*(.+?)\s*[-–]?$/);
  const headerTitle = titleParts ? titleParts[1].trim() : site.title;
  const headerSubtitle = titleParts ? titleParts[2].replace(/[-–]$/, "").trim() : "";

  return (
    <Box
      className="steg-public-project"
      style={{ "--project-primary": primaryColor } as React.CSSProperties}
      minHeight="100vh"
      display="flex"
      flexDirection="column"
      bgcolor="#f7f8fa"
    >
      {isTestEnvironment && (
        <Box
          role="status"
          style={{
            backgroundColor: "#d97706",
            color: "#ffffff",
            padding: "18px 24px",
            textAlign: "center",
            fontWeight: 900,
            fontSize: 20,
            letterSpacing: "0.06em",
            lineHeight: 1.35,
          }}
        >
          VORSCHAU – Diese Website ist nicht öffentlich sichtbar.
        </Box>
      )}
      {showBackToTop && (
        <Button
          className="cms-back-to-top"
          aria-label="Zum Seitenanfang"
          onClick={() => window.scrollTo({ top: 0, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" })}
          style={{ position: "fixed", right: 20, bottom: 20, zIndex: 1100, minWidth: 44, width: 44, minHeight: 44, padding: 0, background: "#fff", color: "#26343f", border: "1px solid #dce2e7", borderRadius: 6, fontWeight: 400, textTransform: "none", boxShadow: "0 2px 8px rgba(0,0,0,.08)" }}
        ><ArrowUpwardIcon style={{ fontSize: 20 }} /></Button>
      )}
      {/* Hero Section */}
      <Box className={`${classes.hero} ${site.headerImage ? "has-image" : ""}`}>
        {site.headerImage && (
          <img className={classes.heroImage} src={getLogoUrl(site.headerImage)} alt="" decoding="async" />
        )}
        <Container className={classes.heroContent}>
        <Box className={classes.heroText}>
        {/* Project identity */}
        {site?.logo && (
          <Box
            className={classes.topLogoContainer}
            {...({
              component: Link,
              to: getRootLink(),
            } as any)}
          >
            <img
              src={getLogoUrl(site?.logo)}
              alt={site?.title}
              decoding="async"
              className={classes.topLogo}
            />
          </Box>
        )}
          <Box className="project-title">
          <Typography
            variant="h2"
            component="h1"
            lang="de"
            gutterBottom
            style={{
              fontWeight: 400,
              fontSize: isMobile ? "1.6rem" : "2.1rem",
              overflowWrap: "break-word",
              hyphens: "none",
              lineHeight: 1.15,
            }}
          >
            {headerTitle}
          </Typography>
          {headerSubtitle && <Typography component="p" style={{ color: "#62717e", fontSize: 16, marginTop: 6 }}>{headerSubtitle}</Typography>}
          {site.description && (
            <Typography
              variant="body1"
              component="p"
              style={{
                color: "#62717e",
                fontSize: 16,
                fontWeight: 400,
                maxWidth: 700,
                lineHeight: 1.5,
              }}
            >
              {site.description}
            </Typography>
          )}
        </Box>
        </Box>
        </Container>
      </Box>

      {/* Sticky Navigation */}
      <Box className={classes.stickyNav}>
        <Container style={{ maxWidth: 1600 }}>
          <Toolbar
            disableGutters
            style={{
              justifyContent: "center",
              minHeight: 52,
            }}
          >
            {!isMobile ? (
              navLinks
            ) : (
              <Box
                display="flex"
                width="100%"
                justifyContent="space-between"
                alignItems="center"
                px={2}
              >
                <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                  {activeTopLevelItem?.title || "Menü"}
                </Typography>
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label="Seitennavigation öffnen"
                  aria-expanded={mobileMenuOpen}
                  onClick={() => setMobileMenuOpen(true)}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </Container>
        {!isMobile && subNavLinks}
      </Box>

      <Drawer className="steg-public-project steg-project-mobile-menu"
        anchor="bottom"
        style={{ "--project-primary": primaryColor } as React.CSSProperties}
        PaperProps={{ role: "dialog", "aria-modal": true, "aria-labelledby": "mobile-navigation-title" }}
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      >
        <Box className="steg-mobile-menu-content" width="100%" p={2}>
          <Box className="steg-mobile-sheet-handle" aria-hidden="true" />
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography id="mobile-navigation-title" component="h2" style={{ fontSize: 23 }}>Seitenübersicht</Typography>
            <IconButton aria-label="Seitennavigation schließen" onClick={() => setMobileMenuOpen(false)}><CloseIcon /></IconButton>
          </Box>
          <List component="nav" aria-label="Projektseiten">
            {navigation.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = expandedMobileMenus.includes(item.id);
              const isItemActive =
                item.urlKey?.toLowerCase() === activePageSlug.toLowerCase() ||
                (activeTopLevelItem?.id === item.id && !item.children?.some(child => child.urlKey?.toLowerCase() === activePageSlug.toLowerCase()));

              return (
                <React.Fragment key={item.id}>
                  <ListItem
                    className="steg-mobile-menu-row"
                    data-active={isItemActive || undefined}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Link
                          to={getLinkTo(item)}
                          aria-current={isItemActive ? "page" : undefined}
                          onClick={() => {
                            setActiveMenuItem(item);
                            setMobileMenuOpen(false);
                          }}
                          style={{
                            color: "inherit",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            textDecoration: "none",
                          }}
                        >
                          <span>{item.title}</span>
                          {isItemActive ? <CheckCircleOutlineIcon className="steg-mobile-selected-icon" fontSize="small" aria-hidden="true" /> : !hasChildren ? <ChevronRightIcon className="steg-mobile-row-arrow" fontSize="small" aria-hidden="true" /> : null}
                        </Link>
                      }
                      primaryTypographyProps={{
                        style: {
                          fontWeight: "normal",
                          borderBottom: isItemActive
                            ? `2px solid ${primaryColor}`
                            : "none",
                          width: "fit-content",
                          color: isItemActive ? primaryColor : "inherit",
                        },
                      }}
                    />
                    {hasChildren && (
                      <IconButton
                        aria-label={`${isExpanded ? "Untermenü schließen" : "Untermenü öffnen"}: ${item.title}`}
                        aria-expanded={isExpanded}
                        onClick={() => toggleMobileMenu(item.id)}
                      >
                        <ExpandMoreIcon
                          style={{
                            transform: isExpanded
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 150ms ease",
                          }}
                        />
                      </IconButton>
                    )}
                  </ListItem>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List
                      component="div"
                      disablePadding
                      className="steg-mobile-submenu"
                    >
                      {item.children?.map((child) => {
                        const isChildActive =
                          activePageSlug === child.urlKey ||
                          child.urlKey?.toLowerCase() ===
                            activePageSlug.toLowerCase();
                        return (
                          <ListItem
                            button
                            key={child.id}
                            className="steg-mobile-menu-row steg-mobile-menu-child"
                            data-active={isChildActive || undefined}
                            aria-current={isChildActive ? "page" : undefined}
                            component={Link}
                            to={getLinkTo(child)}
                            style={{ paddingLeft: 32 }}
                            onClick={() => {
                              setActiveMenuItem(child);
                              setMobileMenuOpen(false);
                            }}
                          >
                            <ListItemText
                              primary={child.title}
                              primaryTypographyProps={{
                                style: {
                                  fontSize: "0.9rem",
                                  fontWeight: "normal",
                                  color: "inherit",
                                },
                              }}
                            />
                            {isChildActive && <CheckCircleOutlineIcon className="steg-mobile-selected-icon" fontSize="small" aria-hidden="true" />}
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      </Drawer>

      <Box
        component="main"
        lang="de"
        flexGrow={1}
        className={classes.section}
        style={{
          minHeight: 500,
        }}
      >
        <Container key={page?.id || "empty-page"} className="steg-project-page-transition" aria-busy={loadingPage} style={{ maxWidth: 1600, opacity: loadingPage ? 0.6 : 1 }}>
          {(site.knowledge_search_enabled ?? site.knowledgeSearchEnabled) === true && site.isPublished !== false && <KnowledgeSearch primaryColor={primaryColor} siteId={site.id} pages={allPages} onOpenPage={source => history.push(`${getBasePath()}/${source.urlKey}`)} />}
          {page?.blocks
            ?.sort((a, b) => a.order - b.order)
            .map((block, index) => (
              <PublicBlockBoundary key={`${page?.id || "page"}-${block.id}`}>
                <ScrollReveal
                  delay={0}
                  eager={index < 2}
                >
                  <Box
                    mb={
                      block.blockType && block.blockType.indexOf("header_") === 0
                        ? 2
                        : isMobile ? 3 : 5
                    }
                  >
                    <CmsBlockRenderer
                      block={block}
                      teamMembers={teamMembersByBlock[block.id] || []}
                      projectLeader={site.projectLeader}
                      siteId={site.id}
                      primaryColor={primaryColor}
                      allPages={allPages}
                      basePath={getBasePath()}
                    />
                  </Box>
                </ScrollReveal>
              </PublicBlockBoundary>
            ))}

          {(!page?.blocks || page.blocks.length === 0) && (
            <Box textAlign="center" py={10}>
              <Typography variant="h5" color="textSecondary">
                {pageError || "Diese Seite hat noch keinen Inhalt."}
              </Typography>
            </Box>
          )}
        </Container>
      </Box>

      <Box component="footer" className={`${classes.footer} steg-project-footer`}>
        <Container maxWidth="lg" style={{ paddingLeft: isMobile ? 0 : undefined, paddingRight: isMobile ? 0 : undefined }}>
          <Box style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", gap: isMobile ? 24 : 40 }}>
            <Box className="steg-footer-identity" style={{ display: "flex", alignItems: "center", gap: 20, minWidth: 0 }}>
              <a href="https://www.steg.de" target="_blank" rel="noopener noreferrer" aria-label="die STEG – zur Website" style={{ display: "inline-flex", flexShrink: 0 }}>
                <img src={stegLogo} alt="die STEG" width={112} height={34} loading="lazy" decoding="async" style={{ objectFit: "contain" }} />
              </a>
              <Box className="steg-footer-project" style={{ borderLeft: "1px solid #e4e8eb", paddingLeft: 20, display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                {site.logo && <img src={getLogoUrl(site.logo)} alt="Gemeindelogo" loading="lazy" decoding="async" style={{ width: 30, height: 34, objectFit: "contain", flexShrink: 0 }} />}
                <Typography variant="caption" style={{ color: "#64717b", lineHeight: 1.6, overflowWrap: "anywhere" }}>{site.title}<br />© {new Date().getFullYear()}</Typography>
              </Box>
            </Box>
            <Box className="steg-footer-actions" style={{ display: "flex", flexDirection: "column", alignItems: isMobile ? "flex-start" : "flex-end", gap: 8 }}>
              <Box className="steg-footer-nav" component="nav" aria-label="Kontakt und rechtliche Informationen" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 20px" }}>
                {hasFooterContact && <Button variant="outlined" size="small" onClick={() => { setContactStatus(null); setContactSubmitted(false); setContactOpen(true); }} style={{ minHeight: 32, padding: "3px 12px", borderColor: primaryColor, color: primaryColor, textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: 6, whiteSpace: "nowrap" }}>Kontakt</Button>}
                <Button component="a" variant="outlined" size="small" href={`mailto:digitale-plattform@steg.de?subject=${encodeURIComponent(`Technisches Problem: ${site.title}`)}`} style={{ minHeight: 32, padding: "3px 12px", borderColor: "#64717b", color: "#64717b", textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: 6 }}>Technische Hilfe</Button>
                <Box className="steg-footer-legal">
                <a href="https://www.steg.de/impressum/" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", minHeight: 36, fontSize: 13 }}>Impressum</a>
                <a href="https://www.steg.de/datenschutz/" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", minHeight: 36, fontSize: 13 }}>Datenschutz</a>
                </Box>
              </Box>
            </Box>
          </Box>
          {site.footerText && <Typography variant="caption" style={{ display: "block", marginTop: 20, color: "#64717b", lineHeight: 1.6 }}>{site.footerText}</Typography>}
        </Container>
      </Box>
      <Dialog className="steg-public-project" open={contactOpen} onClose={() => !contactSending && setContactOpen(false)} fullWidth fullScreen={isMobile} maxWidth="sm" aria-labelledby="contact-dialog-title"
        PaperProps={{ style: { ...(isMobile ? { margin: 0, width: "100%", maxWidth: "100%", height: "100%", maxHeight: "100%", borderRadius: 0 } : {}), minWidth: 0, boxSizing: "border-box" } }}>
        <DialogTitle id="contact-dialog-title" disableTypography style={{ padding: isMobile ? "16px 20px" : "22px 26px 18px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <Typography component="h2" style={{ fontWeight: 800, fontSize: "1.15rem", color: "#243746" }}>Kontakt aufnehmen</Typography>
          <IconButton aria-label="Kontaktformular schließen" disabled={contactSending} onClick={() => setContactOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers style={{ padding: isMobile ? "20px" : "22px 26px", minWidth: 0, minHeight: 0, overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch" }}>
          {contactSubmitted ? (
            <Typography style={{ color: primaryColor, fontWeight: 600, lineHeight: 1.6 }}>
              {contactStatus}
            </Typography>
          ) : <>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 18 }}>Senden Sie uns Ihre Nachricht. Mit * markierte Felder sind erforderlich.</Typography>
            <Box display="grid" gridTemplateColumns={isMobile ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))"} gridGap={16}>
            <TextField aria-hidden="true" tabIndex={-1} autoComplete="off" name="website" value={website} onChange={(event) => setWebsite(event.target.value)} style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }} />
            <Box style={{ gridColumn: isMobile ? "auto" : "1 / -1", paddingBottom: 4 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 800, color: primaryColor }}>Ihre Kontaktdaten</Typography>
            </Box>
            <TextField fullWidth required variant="outlined" size="small" label="Vorname" value={contactForm.firstName} onChange={(event) => setContactForm({ ...contactForm, firstName: event.target.value })} />
            <TextField fullWidth required variant="outlined" size="small" label="Nachname" value={contactForm.lastName} onChange={(event) => setContactForm({ ...contactForm, lastName: event.target.value })} />
            <TextField fullWidth required variant="outlined" size="small" type="email" label="E-Mail" style={{ gridColumn: isMobile ? "auto" : "1 / -1" }} value={contactForm.email} onChange={(event) => setContactForm({ ...contactForm, email: event.target.value })} />
            <Box style={{ gridColumn: isMobile ? "auto" : "1 / -1", borderTop: "1px solid rgba(0,0,0,.14)", paddingTop: 18, marginTop: 4 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 800, color: primaryColor }}>Ihre Nachricht</Typography>
            </Box>
            <TextField fullWidth required variant="outlined" size="small" label="Betreff" style={{ gridColumn: isMobile ? "auto" : "1 / -1" }} value={contactForm.title} onChange={(event) => setContactForm({ ...contactForm, title: event.target.value })} />
            <Box style={{ gridColumn: isMobile ? "auto" : "1 / -1" }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <label htmlFor="contact-message" style={{ fontSize: 14, fontWeight: 500 }}>Nachricht *</label>
              </Box>
              <TextField id="contact-message" required fullWidth multiline InputProps={{ inputComponent: "textarea" }} inputProps={{ rows: 4, style: { resize: "vertical", height: 96, minHeight: 72, overflowY: "auto" } }} variant="outlined" placeholder="Wie können wir Ihnen helfen?" value={contactForm.message} onChange={(event) => setContactForm({ ...contactForm, message: event.target.value })} />
            </Box>
            </Box>
            <FormControlLabel style={{ marginTop: 16, marginLeft: 0, marginRight: 0, alignItems: "flex-start", width: "100%" }} control={<Checkbox checked={contactForm.rulesAccepted} onChange={(event) => setContactForm({ ...contactForm, rulesAccepted: event.target.checked })} style={{ color: primaryColor }} />} label={<span>Ich habe die <a href="https://www.steg.de/datenschutzerklaerung/" target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()} style={{ color: primaryColor, fontWeight: 700, textDecoration: "underline" }}>Datenschutzerklärung der STEG</a> zur Kenntnis genommen.</span>} />
            {contactStatus && <Typography color="error" style={{ marginTop: 8 }}>{contactStatus}</Typography>}
          </>}
        </DialogContent>
        <DialogActions style={{ padding: isMobile ? "16px 20px max(16px, env(safe-area-inset-bottom))" : "14px 24px", flexShrink: 0, display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "auto auto", gap: 10, justifyContent: isMobile ? "stretch" : "end" }}>
          <Button style={{ margin: 0, minHeight: 44, textTransform: "none", order: isMobile ? 2 : 0 }} onClick={() => setContactOpen(false)} disabled={contactSending}>{contactSubmitted ? "Schließen" : "Abbrechen"}</Button>
          {!contactSubmitted && <Button variant="contained" onClick={sendContactMessage} disabled={contactSending || !contactForm.rulesAccepted || !contactForm.firstName.trim() || !contactForm.lastName.trim() || !contactForm.email.trim() || !contactForm.title.trim() || !contactForm.message.trim()} style={{ margin: 0, minHeight: 44, textTransform: "none", backgroundColor: primaryColor, color: "#fff" }}>{contactSending ? "Wird gesendet…" : "Nachricht senden"}</Button>}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
